import React, { useEffect, useState } from 'react'
import { Box, Alert, CircularProgress, Tabs, Tab } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { apiService } from '../services/api'
import SendReportModal from '../components/dashboard/SendReportModal'
import {
  ReportPage, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, Icon, fmt, theme,
  useDistinctFilters, buildOplFilterFields,
} from '../components/report-shell'

const yesterdayIso = () => {
  const d = new Date(); d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

const nextDayIso = (isoDate) => {
  const [year, month, day] = isoDate.split('-').map(Number)
  const d = new Date(Date.UTC(year, month - 1, day + 1))
  return d.toISOString().slice(0, 10)
}

const OrangeShelfSalesPage = () => {
  // Anchor day = end of period (used for MTD / streak / "days since last sale").
  // periodStart..periodEnd defines a separate range whose totals appear as
  // "(okres)" columns; defaulting both to yesterday keeps legacy behavior.
  const [periodStart, setPeriodStart] = useState(yesterdayIso())
  const [periodEnd, setPeriodEnd] = useState(yesterdayIso())
  const [filters, setFilters] = useState({})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(0)
  const [sendModal, setSendModal] = useState({ open: false, scope: null, value: null })

  const distinct = useDistinctFilters('opl')
  const day = periodEnd
  const isRange = periodStart !== periodEnd
  const allFilters = () => ({
    day, period_start: periodStart, period_end: periodEnd, ...filters,
  })

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const resp = await apiService.getOrangeShelfSales(allFilters())
      setData(resp.data)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const exportExcel = async () => {
    setExporting(true)
    try {
      const resp = await apiService.exportOrangeShelfSales(allFilters())
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      const fnameSuffix = isRange ? `${periodStart}_do_${periodEnd}` : day
      link.setAttribute('download', `Salony_polkowe_OPL_${fnameSuffix}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setExporting(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [periodStart, periodEnd, JSON.stringify(filters)])

  const T = data?.totals || {}

  const rollupCols = (labelField, label, scope) => [
    {
      field: '_send', header: '', width: '40px', sortable: false,
      render: (_v, row) => {
        const value = row[scope] || row.region || row.dystrykt || row.agent || row.kam
        if (!value || value === '(brak)') return null
        return (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); setSendModal({ open: true, scope, value }) }}
            title={`Wyślij raport R2 (${scope})`}
            style={{
              display: 'inline-flex', cursor: 'pointer',
              color: theme.textSub, padding: 4, borderRadius: 5,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.accent }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.textSub }}
          >
            <EmailIcon fontSize="small" sx={{ width: 14, height: 14 }} />
          </span>
        )
      },
    },
    { field: labelField, header: label, width: '1.5fr', minWidth: 180 },
    { field: 'salon_count', header: 'Salonów', width: '95px', numeric: true },
    { field: 'opos_day', header: 'OPOS (dzień)', width: '120px', sales: true },
    { field: 'loc_day', header: 'Loc (dzień)', width: '110px', sales: true },
    { field: 'total_day', header: 'TOTAL (dzień)', width: '120px', sales: true },
    ...(isRange ? [
      { field: 'opos_period', header: 'OPOS (okres)', width: '120px', sales: true },
      { field: 'loc_period', header: 'Loc (okres)', width: '110px', sales: true },
      { field: 'total_period', header: 'TOTAL (okres)', width: '125px', sales: true },
    ] : []),
    { field: 'total_mtd', header: 'TOTAL (MTD)', width: '115px', numeric: true },
  ]

  const cols = [
    { field: 'alert', header: '⚠', width: '50px', sortable: false,
      render: (v) => v ? <span title={v}>🚨</span> : '' },
    { field: 'kod_bscs', header: 'BSCS', width: '110px' },
    { field: 'city', header: 'Miasto', width: '140px' },
    { field: 'kam', header: 'KAM', width: '110px' },
    { field: 'agent', header: 'Agent', width: '1.6fr', minWidth: 180 },
    { field: 'rksa', header: 'RKSA', width: '160px' },
    { field: 'xdm', header: 'XDM', width: '150px' },
    { field: 'opos_day', header: 'OPOS (dz)', width: '105px', sales: true },
    { field: 'loc_day',  header: 'Loc (dz)',  width: '95px',  sales: true },
    { field: 'total_day', header: 'TOTAL (dz)', width: '105px', sales: true },
    ...(isRange ? [
      { field: 'opos_period', header: 'OPOS (okres)', width: '115px', sales: true },
      { field: 'loc_period',  header: 'Loc (okres)',  width: '105px', sales: true },
      { field: 'total_period', header: 'TOTAL (okres)', width: '115px', sales: true },
    ] : []),
    { field: 'total_mtd', header: 'TOTAL (MTD)', width: '105px', numeric: true },
    {
      field: 'days_since_last_sale', header: 'Dni bez sprzedaży', width: '140px', numeric: true,
      render: (v) => v == null ? '—' : (
        <span style={{
          color: v >= 3 ? '#dc2626' : '#1c1917',
          fontWeight: v >= 3 ? 700 : 500,
        }}>{v}</span>
      ),
    },
  ]

  const kpiTiles = data ? [
    { key: 'all',   label: 'Salony półkowe',     value: fmt.int(T.salon_count || 0) },
    { key: 'sold',  label: 'Sprzedały dziś',     value: `${T.salons_with_sale_today || 0} / ${T.salon_count || 0}`, accent: '#16a34a' },
    { key: 'zero',  label: 'Bez sprzedaży dziś', value: fmt.int(T.salons_zero_today || 0), accent: '#dc2626' },
    { key: 'today', label: 'Sprzedaż dnia',      value: fmt.int(T.total_day || 0), unit: 'szt.',
      sub: `OPOS ${fmt.int(T.opos_day || 0)} · Loc ${fmt.int(T.loc_day || 0)}` },
    ...(isRange ? [{
      key: 'period', label: `Sprzedaż okresu (${data.period_days} dni)`,
      value: fmt.int(T.total_period || 0), unit: 'szt.',
      sub: `OPOS ${fmt.int(T.opos_period || 0)} · Loc ${fmt.int(T.loc_period || 0)}`,
      accent: '#2563eb',
    }] : []),
  ] : []

  const tabConfigs = [
    { label: `Per region (${data?.regions?.length || 0})`, rows: data?.regions || [], cols: rollupCols('region', 'Region', 'region'), keyField: 'region' },
    { label: `Per dystrykt (${data?.districts?.length || 0})`, rows: data?.districts || [], cols: rollupCols('dystrykt', 'Dystrykt', 'district'), keyField: 'dystrykt' },
    { label: `Per agent (${data?.agents?.length || 0})`, rows: data?.agents || [], cols: rollupCols('agent', 'Agent', 'agent'), keyField: 'agent' },
    { label: `Per KAM (${data?.kams?.length || 0})`, rows: data?.kams || [], cols: rollupCols('kam', 'KAM', 'kam'), keyField: 'kam' },
    { label: `Salony (${data?.salons?.length || 0})`, rows: data?.salons || [], cols, keyField: 'plant_code' },
  ]
  const active = tabConfigs[tab]

  const filterBar = (
    <FilterBar
      periods={[
        {
          type: 'dateRange', key: 'period',
          labelFrom: 'Okres od', labelTo: 'Do',
          valueFrom: periodStart, valueTo: periodEnd,
          onChangeFrom: (v) => {
            const next = v || yesterdayIso()
            setPeriodStart(next)
            if (next > periodEnd) setPeriodEnd(next)
          },
          onChangeTo: (v) => {
            const next = v || yesterdayIso()
            setPeriodEnd(next)
            if (next < periodStart) setPeriodStart(next)
          },
        },
      ]}
      periodRight={data?.month_start ? `MTD od ${data.month_start}` : null}
      filters={buildOplFilterFields(distinct, filters, setFilters)}
      onReset={() => setFilters({})}
    />
  )

  return (
    <ReportPage
      actions={
        <>
          <HeaderButton onClick={fetchData} disabled={loading}>
            <Icon.refresh /> {loading ? 'Ładowanie…' : 'Odśwież'}
          </HeaderButton>
          <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
            <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
          </HeaderButton>
        </>
      }
      filterBar={filterBar}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && !loading && (
        <Section
          title="Salony półkowe (Panel Bezpieczeństwa)"
          right={
            <Box sx={{ borderBottom: 0 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
                {tabConfigs.map((t, i) => <Tab key={i} label={t.label} />)}
              </Tabs>
            </Box>
          }
        >
          <ReportTable
            rows={active.rows.map((r, i) => ({ id: r[active.keyField] || i, ...r }))}
            columns={active.cols}
            initialSort={
              tab === 4
                ? { field: isRange ? 'total_period' : 'total_day', dir: 'desc' }
                : { field: isRange ? 'total_period' : 'total_mtd', dir: 'desc' }
            }
            pageSize={tab === 4 ? 50 : 25}
            pageSizeOptions={[25, 50, 100, 500]}
            density="compact"
          />
        </Section>
      )}

      <SendReportModal
        open={sendModal.open}
        onClose={() => setSendModal({ open: false, scope: null, value: null })}
        operator="orange"
        scope={sendModal.scope}
        scopeValue={sendModal.value}
        reportType="orange_shelf_sales"
        periodStart={day}
        periodEnd={nextDayIso(day)}
      />
    </ReportPage>
  )
}

export default OrangeShelfSalesPage
